import _ from "lodash";
import React, { useState, useEffect } from "react";
import classes from "./LinkifyModal.scss";
import I18nHOC from "UIComponents/I18nHOC";
import CreateDropDown from "Attachment/CreateDropDown";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { isURL, getThumbUrl, htmlToText } from "Utils";
import {
  TextInput,
  Button,
  MediaCard,
  IconButton,
  Modal,
  RadioButton,
} from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";

const DROP_DOWN_OPTIONS = [
  { key: "upload_device", label: "upload_device" },
  { key: "add_google_drive", label: "add_google_drive" },
  { key: "add_one_drive", label: "add_one_drive" },
];

const RADIO_BUTTONS = [
  { label: "common:add_url_uppercase", value: "URL" },
  { label: "common:upload_a_file", value: "FILE" },
];

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
};

const modalStyles = {
  modalBodyStyle: {
    padding: "8px 16px",
  },
  modalHeaderStyle: {
    padding: "12px 16px",
  },
};

const buttonDropDownStyles = {
  buttonDropdownParentContainerStyle: {
    width: "100%",
  },
  buttonDropdownButtonComponentStyle: {
    height: "100%",
  },
  buttonDropDownContainerStyle: {
    top: "52px",
    width: "100%",
  },
};

const hoverContainerStyles = {
  width: "100%",
};

const inputRefs = {};

const getRadioButtons = ({
  t,
  onClickRadioButton,
  selectedButton,
  isLoading,
  testId,
}) => {
  return (
    <div className={classes.radioButtonContainer}>
      {_.map(RADIO_BUTTONS, button => (
        <RadioButton
          dsVersion="2.0"
          key={button.value}
          checked={selectedButton === button.value}
          disabled={isLoading}
          onChange={() => onClickRadioButton(button.value)}
          size="medium"
          testId={testId}
        >
          {t(button.label)}
        </RadioButton>
      ))}
    </div>
  );
};

const LinkifyModal = ({
  url: urlFromProp,
  title: titleFromProp = "",
  linkType: linkTypeFromProp,
  filename: filenameFromProp,
  mimeType: mimeTypeFromProp,
  fileType: fileTypeFromProp,
  onClickSave,
  onClose,
  t,
  setToastMsg,
  testId,
  customFilterFilesHandler,
}) => {
  // states
  const [urlTypeState, setUrlTypeState] = useState({});
  const [fileTypeState, setFileTypeState] = useState({});
  const [linkType, setLinkType] = useState("URL");
  const [title, setTitle] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [inputFieldErrors, setInputFieldErrors] = useState({
    url: "",
    title: "",
  });

  // vars
  let state = {};
  if (linkType === "URL") {
    state = urlTypeState;
  }
  if (linkType === "FILE") {
    state = fileTypeState;
  }
  const { filename, url, mimeType, fileType } = state;

  useEffect(() => {
    const lType = linkTypeFromProp || "URL";
    if (lType === "URL") {
      setUrlTypeState({
        url: urlFromProp,
      });
    } else if (lType === "FILE") {
      setFileTypeState({
        url: urlFromProp,
        filename: filenameFromProp,
        mimeType: mimeTypeFromProp,
        fileType: fileTypeFromProp,
      });
    }
    setLinkType(lType);
    setTitle(titleFromProp);
  }, []);

  const updateUrlTypeState = params => {
    setUrlTypeState(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const updateFileTypeState = params => {
    setFileTypeState(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const updateState = params => {
    switch (linkType) {
      case "URL": {
        updateUrlTypeState(params);
        break;
      }
      case "FILE": {
        updateFileTypeState(params);
        break;
      }
      default: {
        break;
      }
    }
  };

  const onSave = () => {
    if (validateInputFields()) {
      let errors = 0;
      _.forEach(inputRefs, ref => {
        if (ref && !ref.value) {
          errors++;
        }
      });
      // validate if there is no file uploaded
      if (!_.trim(url)) {
        errors += 1;
        setError(t("common:please_upload_a_file"));
      }
      if (errors > 0) {
        setToastMsg({
          msg: "toastMsgs:please_fill_required_fields",
          type: "warning",
          position: "toast-top-center",
        });
        return;
      }
      const params = {
        ...state,
        linkType: linkType,
        title,
      };
      if (onClickSave) {
        onClickSave(params);
      }
    }
  };

  const onClickRadioButton = value => {
    setLinkType(value);
  };

  const onAddAttachment = params => {
    const attachment = _.get(params, "0", {});
    const { url, mimeType, name: filename, type } = attachment;
    updateState({ url, filename, mimeType, fileType: type });
    setError("");
  };

  const { thumbUrl, isIcon } = getThumbUrl({
    attachment: { ...state, type: state.fileType },
    width: 40,
    height: 40,
  });

  const validateInputFields = () => {
    if (!isURL(state?.url)) {
      setInputFieldErrors({
        title: "",
        url: t("common:web_link_not_valid"),
      });
      return false;
    } else if (!title) {
      setInputFieldErrors({
        url: "",
        title: t("common:error"),
      });
      return false;
    } else {
      setInputFieldErrors({
        url: "",
        title: "",
      });
      return true;
    }
  };

  const getUrlContainer = () => {
    switch (linkType) {
      case "URL": {
        return (
          <div style={containerStyle}>
            <TextInput
              dsVersion="2.0"
              value={url}
              name={"url"}
              onChange={e => updateState({ url: e.target.value })}
              label={t("common:url")}
              isRequired={true}
              ref={ref => (inputRefs["url"] = ref)}
              placeholder={t("common:enter_with_label", {
                label: t("common:url"),
              })}
              error={inputFieldErrors.url}
              size={"medium"}
              testId={`${testId}:url`}
            />
            {inputFieldErrors.url && (
              <div className={classes.errorText}>{inputFieldErrors.url}</div>
            )}
          </div>
        );
      }
      case "FILE": {
        const isUploaded = url && mimeType;
        // eslint-disable-next-line no-restricted-properties
        const windowOpen = window.open;
        return isUploaded ? (
          <div className={classes.uploadButtonContainer}>
            <MediaCard
              dsVersion="2.0"
              cardType="medium"
              name={htmlToText(filename) || t("common:untitled")}
              type={fileType}
              mimeType={mimeType}
              url={url}
              thumbUrl={isIcon ? undefined : thumbUrl}
              showActionsOnHover
              onClick={() => windowOpen && windowOpen(url, "_blank").focus()}
              options={
                <IconButton
                  dsVersion="2.0"
                  onClick={() => updateState({ url: "" })}
                  icon={<DeleteOutlined variant="subtle" size="xx-small" />}
                  variant="neutral"
                  shouldStopPropagation
                  testId={`${testId}:delete`}
                />
              }
              testId={testId}
            />
          </div>
        ) : (
          <div className={classes.uploadButtonContainer}>
            <CreateDropDown
              dropDownOptions={DROP_DOWN_OPTIONS}
              buttonComponent={
                <Button
                  dsVersion="2.0"
                  size="medium"
                  variant="neutral"
                  type="outlined"
                  disabled={isLoading}
                  isFullWidth
                  testId={`${testId}:create-dropdown`}
                >
                  {t("common:upload_with_label", {
                    label: t("common:lowercase", {
                      text: t("common:file"),
                    }),
                  })}
                </Button>
              }
              updateLoadingState={setLoading}
              addAttachment={onAddAttachment}
              {...buttonDropDownStyles}
              multiselect={false}
              hoverContainerStyles={hoverContainerStyles}
              customFilterFilesHandler={customFilterFilesHandler}
              shouldReturnSignedUrl={true}
            />
            {error && <div className={classes.uploadError}>{error}</div>}
          </div>
        );
      }
    }
  };

  const headerTitle = !urlFromProp
    ? t("common:insert_with_label", {
        label: t("common:lowercase", { text: t("common:link") }),
      })
    : t("common:edit_link");

  return (
    <Modal dsVersion="2.0" onClose={onClose} width="410px" height="426px">
      <Modal.Header
        isClosable={true}
        hasSeparator={false}
        title={headerTitle}
      />

      <Modal.Body style={modalStyles.modalBodyStyle}>
        <div className={classes.modalBodyContainer}>
          {getRadioButtons({
            t,
            onClickRadioButton,
            selectedButton: linkType,
            isLoading,
            testId,
          })}
          <div className={classes.inputsContainer}>
            {getUrlContainer()}
            <TextInput
              dsVersion="2.0"
              value={title}
              onChange={e => setTitle(e.target.value)}
              label={t("common:text_to_display")}
              isRequired={true}
              ref={ref => (inputRefs["title"] = ref)}
              placeholder={t("common:enter_text_to_display")}
              error={inputFieldErrors.title}
              size="medium"
              testId={`${testId}:title`}
            />
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <footer className={classes.buttonsContainer}>
          <Button
            dsVersion="2.0"
            size="medium"
            onClick={onClose}
            type="outlined"
            variant="neutral"
            testId={`${testId}:cancel`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            dsVersion="2.0"
            size="medium"
            disabled={isLoading || !url || !title}
            onClick={onSave}
            variant={urlFromProp ? "destructive" : "progressive"}
            testId={`${testId}:save`}
          >
            {urlFromProp ? t("common:update") : t("equationEditor:insert")}
          </Button>
        </footer>
      </Modal.Footer>
    </Modal>
  );
};

const mapActionCreators = {
  setToastMsg,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(null, mapActionCreators)
)(LinkifyModal);
